import React, { useState, useEffect } from "react";
import { Container, Form, Row, Col } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "./UnifiedFormStyles.css";
import AppButton from "../../Components/MerchantButton";
import { compressAndUpload } from "../../Helpers/Utils/photoUpload";

function AwardingFlagship({ navigation, primaryDetails, setPrimaryDetails }) {
  const [isError, setIsError] = useState({
    flagship_freebies: false,
    total_eco_bag: false,
    total_ballpen: false,
    total_notebook: false,
    total_fan: false,
    total_calculator: false,
    total_mug: false,
    flagship_receiver_name: false,
    flagship_receiver_position: false,
  });

  const [isUploading, setIsUploading] = useState(false);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo exceeds 5MB limit. Please retake the photo.", {
        autoClose: 3000, hideProgressBar: true, theme: "colored"
      });
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    try {
      const uploadResult = await compressAndUpload(file);
      if (!uploadResult.success) {
        toast.error(uploadResult.error || "Upload failed. Please try again.");
        e.target.value = '';
        return;
      }
      setPrimaryDetails(prev => ({
        ...prev,
        flagship_freebies_temp_path: uploadResult.temp_path
      }));
      if (isError.flagship_freebies) {
        setIsError(prev => ({ ...prev, flagship_freebies: false }));
      }
    } catch (error) {
      toast.error("Error uploading photo");
    } finally {
      setIsUploading(false);
    }
  };

  // Sync to primaryDetails whenever state changes
  useEffect(() => {
    setPrimaryDetails((prev) => ({
      ...prev,
    }));
  }, [setPrimaryDetails]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    if (type === "file") return; // photos handled by handlePhotoUpload
    setPrimaryDetails(prev => ({ ...prev,
      [name]: value,
    }));
    if (isError[name]) {
      setIsError({
        ...isError,
        [name]: false,
      });
    }
  };

  const validateForm = () => {
    const errors = {
      flagship_freebies: !primaryDetails.flagship_freebies_temp_path,
      total_eco_bag: !primaryDetails.total_eco_bag,
      total_ballpen: !primaryDetails.total_ballpen,
      total_notebook: !primaryDetails.total_notebook,
      total_fan: !primaryDetails.total_fan,
      total_calculator: !primaryDetails.total_calculator,
      total_mug: !primaryDetails.total_mug,
      flagship_receiver_name: !primaryDetails.flagship_receiver_name,
      flagship_receiver_position: !primaryDetails.flagship_receiver_position,
    };

    setIsError(errors);

    const hasErrors = Object.values(errors).some((error) => error);
    if (hasErrors) {
      toast.error("Please fill in all required fields.", {
        autoClose: 3000,
        hideProgressBar: true,
        theme: "colored",
      });
      return false;
    }
    return true;
  };

  const isFormValid = () => {
    return !!primaryDetails.flagship_freebies_temp_path &&
      !!primaryDetails.total_eco_bag &&
      !!primaryDetails.total_ballpen &&
      !!primaryDetails.total_notebook &&
      !!primaryDetails.total_fan &&
      !!primaryDetails.total_calculator &&
      !!primaryDetails.total_mug &&
      !!primaryDetails.flagship_receiver_name &&
      !!primaryDetails.flagship_receiver_position;
  };
  const handleNext = () => {
    if (validateForm()) {
      if (navigation && navigation.go) {
        navigation.go("flagship-feedback");
      }
    }
  };

  const handleBack = () => {
    if (navigation && navigation.previous) {
      navigation.previous();
    }
  };

  return (
    <div className="form-bg">
      <Container>
        <ToastContainer />
        <Row className="justify-content-md-center">
          <Col md={10}>
            <div className="form-main-header">
              <h4 className="form-title">Awarding Flagship</h4>
              <p className="form-desc">Please provide the flagship awarding details</p>
            </div>
            <div className="form-main">
              <div className="form-main-content">
                <Form>

                  {/* Upload Awarding Photo (CERTIFICATE+FREEBIES) */}
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      Upload Awarding Photo (Certificate + Freebies) <span className="form-required">*</span>
                      {primaryDetails.flagship_freebies_temp_path && !isUploading && (
                        <span style={{ marginLeft: '10px', color: '#28a745' }}>✅ Uploaded</span>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      isInvalid={isError.flagship_freebies}
                      disabled={isUploading}
                    />
                    {isUploading && <div style={{ color: '#007bff', fontSize: '13px', marginTop: '6px' }}>⏳ Uploading...</div>}
                    {isError.flagship_freebies && (
                      <div className="form-error">Required</div>
                    )}
                  </Form.Group>

                  {/* How many Eco Bag given? */}
                  <Form.Group className="mb-4" controlId="total_eco_bag">
                    <Form.Label className="form-label">
                      How Many Eco Bag Given? <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="total_eco_bag"
                      value={primaryDetails.total_eco_bag || ""}
                      onChange={handleInputChange}
                      placeholder="Enter quantity"
                      className={`form-input ${isError.total_eco_bag ? "error" : ""}`}
                      min="0"
                    />
                    {isError.total_eco_bag && (
                      <div className="form-error">Please enter quantity.</div>
                    )}
                  </Form.Group>

                  {/* How many Ballpen given? */}
                  <Form.Group className="mb-4" controlId="total_ballpen">
                    <Form.Label className="form-label">
                      How Many Ballpen Given? <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="total_ballpen"
                      value={primaryDetails.total_ballpen || ""}
                      onChange={handleInputChange}
                      placeholder="Enter quantity"
                      className={`form-input ${isError.total_ballpen ? "error" : ""}`}
                      min="0"
                    />
                    {isError.total_ballpen && (
                      <div className="form-error">Please enter quantity.</div>
                    )}
                  </Form.Group>

                  {/* How many Notebook given? */}
                  <Form.Group className="mb-4" controlId="total_notebook">
                    <Form.Label className="form-label">
                      How Many Notebook Given? <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="total_notebook"
                      value={primaryDetails.total_notebook || ""}
                      onChange={handleInputChange}
                      placeholder="Enter quantity"
                      className={`form-input ${isError.total_notebook ? "error" : ""}`}
                      min="0"
                    />
                    {isError.total_notebook && (
                      <div className="form-error">Please enter quantity.</div>
                    )}
                  </Form.Group>

                  {/* How many Fan given? */}
                  <Form.Group className="mb-4" controlId="total_fan">
                    <Form.Label className="form-label">
                      How Many Fan Given? <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="total_fan"
                      value={primaryDetails.total_fan || ""}
                      onChange={handleInputChange}
                      placeholder="Enter quantity"
                      className={`form-input ${isError.total_fan ? "error" : ""}`}
                      min="0"
                    />
                    {isError.total_fan && (
                      <div className="form-error">Please enter quantity.</div>
                    )}
                  </Form.Group>

                  {/* How many Calculator given? */}
                  <Form.Group className="mb-4" controlId="total_calculator">
                    <Form.Label className="form-label">
                      How Many Calculator Given? <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="total_calculator"
                      value={primaryDetails.total_calculator || ""}
                      onChange={handleInputChange}
                      placeholder="Enter quantity"
                      className={`form-input ${isError.total_calculator ? "error" : ""}`}
                      min="0"
                    />
                    {isError.total_calculator && (
                      <div className="form-error">Please enter quantity.</div>
                    )}
                  </Form.Group>

                  {/* How many Mug given? */}
                  <Form.Group className="mb-4" controlId="total_mug">
                    <Form.Label className="form-label">
                      How Many Mug Given? <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="total_mug"
                      value={primaryDetails.total_mug || ""}
                      onChange={handleInputChange}
                      placeholder="Enter quantity"
                      className={`form-input ${isError.total_mug ? "error" : ""}`}
                      min="0"
                    />
                    {isError.total_mug && (
                      <div className="form-error">Please enter quantity.</div>
                    )}
                  </Form.Group>

                  {/* Name of receiver */}
                  <Form.Group className="mb-4" controlId="flagship_receiver_name">
                    <Form.Label className="form-label">
                      Name of Receiver <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="flagship_receiver_name"
                      value={primaryDetails.flagship_receiver_name || ""}
                      onChange={handleInputChange}
                      placeholder="Enter receiver name"
                      className={`form-input ${isError.flagship_receiver_name ? "error" : ""}`}
                    />
                    {isError.flagship_receiver_name && (
                      <div className="form-error">Please enter receiver name.</div>
                    )}
                  </Form.Group>

                  {/* Position of receiver */}
                  <Form.Group className="mb-4" controlId="flagship_receiver_position">
                    <Form.Label className="form-label">
                      Position of Receiver <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="flagship_receiver_position"
                      value={primaryDetails.flagship_receiver_position || ""}
                      onChange={handleInputChange}
                      placeholder="Enter receiver position"
                      className={`form-input ${isError.flagship_receiver_position ? "error" : ""}`}
                    />
                    {isError.flagship_receiver_position && (
                      <div className="form-error">Please enter receiver position.</div>
                    )}
                  </Form.Group>

                  {/* Navigation Buttons */}
                  <div className="form-btn-row">
                    <AppButton
                      onClick={handleBack}
                      variant="back"
                      className="form-btn variant-secondary"
                    >
                      ← Back
                    </AppButton>
                    <AppButton
                      onClick={handleNext}
                      variant="next"
                      className="form-btn variant-primary"
                      disabled={!isFormValid()}
                    >
                      Next →
                    </AppButton>
                  </div>

                </Form>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default AwardingFlagship;
